import { useAnalytics } from "@/analytics/AnalyticsContext";
import { VISITOR_EVENTS } from "@/analytics/events";
import CancelIcon from "@/assets/xmark.png";
import { customToast } from "@/components/Common/Toast";
import {
  LegalDocumentType,
  useAcceptAllLegalTermsMutation,
  useGetAllLegalDocumentsQuery,
} from "@/store/legalDocumentsApiSlice";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import Button from "../../Common/Buttons/Button";
import Modal from "../../Common/Modals";
import IntroductionSkeleton from "../../Common/Skeletons/IntroductionSkeleton";
import {
  BenefitsList,
  DateOfBirthAlert,
  FAQComponent,
  LegalModals,
  RequiredInformation,
  TermsAgreement,
  WelcomeSection,
} from "./components";
import {
  useDateOfBirthValidation,
  useModalState,
  useTermsOfService,
  useUserBenefits,
} from "./hooks";

const Introduction: React.FC<{ handleNext: () => void }> = ({ handleNext }) => {
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.user);
  const { trackEvent } = useAnalytics();
  const [modalContentHeight, setModalContentHeight] = useState<
    number | undefined
  >(undefined);

  const {
    data: legalDocuments,
    isLoading: isLoadingDocuments,
    error: documentsError,
  } = useGetAllLegalDocumentsQuery();

  const [acceptAllLegalTerms, { isLoading: isAcceptingTerms }] =
    useAcceptAllLegalTermsMutation();

  const { userData, isLoading, error } = useUserBenefits();
  const { modalState, toggleModal } = useModalState();
  const {
    agreedToTerms,
    handleTermsChange,
    allTermsAccepted,
    saveTermsAcceptanceAfterSubmit,
  } = useTermsOfService();

  const { dateOfBirth, setDateOfBirth, isSubmitting, validateAndSubmit } =
    useDateOfBirthValidation({
      userData,
      onSuccess: () => {
        saveTermsAcceptanceAfterSubmit();
        toggleModal("info");
        handleNext();
      },
    });

  const handleSubmit = async () => {
    trackEvent(VISITOR_EVENTS.USER_BEGINS_ONBOARDING, {
      user_id: user.id,
      username: user.username,
    });

    if (user.isDefaultDateOfBirth) {
      toggleModal("info");
    } else {
      await handleAcceptLegalTerms();
    }
  };

  const handleAcceptLegalTerms = async () => {
    if (!allTermsAccepted || !legalDocuments) {
      return;
    }

    try {
      const documentVersions: Record<LegalDocumentType, number> = {
        "terms-of-service": legalDocuments["terms-of-service"].version,
        "privacy-policy": legalDocuments["privacy-policy"].version,
      };

      await acceptAllLegalTerms(documentVersions).unwrap();

      saveTermsAcceptanceAfterSubmit();

      handleNext();
    } catch {
      customToast("Error accepting legal terms", "error");
    }
  };

  if (isLoading || isLoadingDocuments) {
    return <IntroductionSkeleton />;
  }

  if (error || documentsError) {
    return (
      <div className="text-[18px] shadow-introBox font-outfit bg-white p-[32px] w-full md:w-[640px] rounded-[16px]">
        <div className="text-red-600">
          {error || "Failed to load legal documents"}
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        id="introduction"
        className="text-[18px] shadow-introBox font-outfit bg-white p-[32px] w-full md:w-[640px] rounded-[16px]"
      >
        <WelcomeSection />

        <BenefitsList userData={userData} />

        <p
          className="cursor-pointer text-[16px] text-[#00ADAC] underline"
          onClick={() => toggleModal("faqs")}
        >
          {t("onboarding.intro.faq_link")}
        </p>

        <TermsAgreement
          agreedToTerms={agreedToTerms}
          onTermsChange={handleTermsChange}
          onToggleModal={toggleModal}
        />

        {user.isDefaultDateOfBirth && <DateOfBirthAlert />}

        <div className="w-full md:w-[218px]">
          <Button
            iconVisibility="show"
            ariaLabel="next"
            label={t("onboarding.intro.begin_setup_button")}
            loading={isAcceptingTerms}
            isDisabled={!allTermsAccepted || isAcceptingTerms}
            handleClick={handleSubmit}
          />
        </div>
      </div>

      {/** Modals */}
      <Modal isOpen={modalState.faqs} onCancel={() => toggleModal("faqs")}>
        <FAQComponent
          onCancel={() => toggleModal("faqs")}
          cancelIcon={CancelIcon}
        />
      </Modal>

      {/* Legal Document Modals */}
      <LegalModals
        modalState={modalState}
        toggleModal={toggleModal}
        cancelIcon={CancelIcon}
      />

      <Modal
        widthSize="md:w-[520px]"
        isOpen={modalState.info}
        onCancel={() => toggleModal("info")}
        contentClassName="introduction-modal-content overflow-y-auto min-h-[300px]"
        style={
          modalContentHeight ? { height: modalContentHeight + 100 } : undefined
        }
      >
        <RequiredInformation
          dateOfBirth={dateOfBirth}
          setDateOfBirth={(value) => {
            setDateOfBirth(value);
            setModalContentHeight(0);
          }}
          onCancel={() => toggleModal("info")}
          onSubmit={validateAndSubmit}
          isSubmitting={isSubmitting}
          cancelIcon={CancelIcon}
          onDatePickerHeightChange={(height) => setModalContentHeight(height)}
        />
      </Modal>
    </>
  );
};

export default Introduction;
